import ProfileImage from "@/components/ProfileImage";
import SpotifyIcon from "@/fragments/SpotifyIcon";
import TikTokIcon from "@/fragments/TikTokIcon";
import { updateUser } from "@/services/api";
import { Close, EmailOutlined, Instagram } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Typography,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  TextField,
  Toolbar,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import { useCurrentUser } from "@/services/hooks";
import { useNavigate } from "react-router-dom";
import AlertSnackbar from "@/components/AlertSnackbar";
import debounce from "lodash/debounce";

export default function Profile() {
  const { data: currentUser, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  // Initialize state with empty values
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [spotify, setSpotify] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [gender, setGender] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const queryClient = useQueryClient();

  // Update state when currentUser data is loaded
  useEffect(() => {
    if (currentUser) {
      setName(currentUser.name || "");
      setBio(currentUser.meta?.bio || "");
      setEmail(currentUser.meta?.email || "");
      setInstagram(currentUser.meta?.instagram || "");
      setSpotify(currentUser.meta?.spotify || "");
      setTiktok(currentUser.meta?.tiktok || "");
      setBirthMonth(currentUser.meta?.birth_month || "");
      setBirthDay(currentUser.meta?.birth_day || "");
      setZipCode(currentUser.meta?.zip_code || "");
      setGender(currentUser.meta?.gender || "");
    }
  }, [currentUser]);

  const GENDERS = ["Female", "Male", "Other"];

  const mutation = useMutation({
    mutationFn: (updates) =>
      updateUser(updates.name, {
        spotify: updates.spotify,
        email: updates.email,
        instagram: updates.instagram,
        tiktok: updates.tiktok,
        bio: updates.bio,
        birth_month: updates.birth_month,
        birth_day: updates.birth_day,
        zip_code: updates.zip_code,
        gender: updates.gender,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["current_user"], data);
      setShowSnackbar(true);
    },
  });

  const debouncedSave = useCallback(
    debounce((updates) => {
      mutation.mutate(updates);
    }, 1000),
    [],
  );

  const handleChange = (field, value) => {
    switch (field) {
      case "name":
        setName(value);
        break;
      case "bio":
        setBio(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "instagram":
        setInstagram(value);
        break;
      case "spotify":
        setSpotify(value);
        break;
      case "tiktok":
        setTiktok(value);
        break;
      case "birth_month":
        setBirthMonth(value);
        break;
      case "birth_day":
        setBirthDay(value);
        break;
      case "zip_code":
        setZipCode(value);
        break;
      case "gender":
        setGender(value);
        break;
    }

    debouncedSave({
      name,
      bio,
      email,
      instagram,
      spotify,
      tiktok,
      birth_month: birthMonth,
      birth_day: birthDay,
      zip_code: zipCode,
      gender,
      [field]: value,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 15 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AlertSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />

      <Box>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, pt: 2 }}>
          <ProfileImage />

          <TextField
            label="Name"
            variant="outlined"
            value={name}
            required
            fullWidth
            disabled={mutation.isPending}
            onChange={(e) => handleChange("name", e.target.value)}
            sx={{ my: 2 }}
          />
        </Box>

        <TextField
          label="A Note"
          placeholder="A little bit about me..."
          variant="outlined"
          multiline
          minRows={2}
          maxRows={4}
          value={bio}
          sx={{ my: 2 }}
          fullWidth
          disabled={mutation.isPending}
          onChange={(e) => handleChange("bio", e.target.value)}
        />

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <TextField
            label={<EmailOutlined color="white" />}
            fullWidth
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => handleChange("email", e.target.value)}
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          />
          <TextField
            label={<Instagram color="white" />}
            fullWidth
            placeholder="https://www.instagram.com/YOUR_USERNAME"
            value={instagram}
            onChange={(e) => handleChange("instagram", e.target.value)}
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          />
          <TextField
            label={<SpotifyIcon />}
            fullWidth
            placeholder="https://open.spotify.com/artist/YOUR_ID"
            value={spotify}
            onChange={(e) => handleChange("spotify", e.target.value)}
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          />
          <TextField
            label={<TikTokIcon />}
            fullWidth
            placeholder="https://www.tiktok.com/@YOUR_USERNAME"
            value={tiktok}
            onChange={(e) => handleChange("tiktok", e.target.value)}
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          />
          <TextField
            label="Home Zip Code"
            variant="outlined"
            value={zipCode}
            onChange={(e) => handleChange("zip_code", e.target.value)}
            fullWidth
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          />

          <TextField
            label="Gender"
            variant="outlined"
            select
            value={gender}
            fullWidth
            onChange={(e) => handleChange("gender", e.target.value)}
            sx={{ my: 2 }}
            disabled={mutation.isPending}
          >
            {GENDERS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{
            my: 2,
            display: "flex",
            gap: 2,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TextField
            label="Month"
            variant="outlined"
            type="number"
            helperText="Birthday"
            value={birthMonth}
            onChange={(e) => handleChange("birth_month", e.target.value)}
            inputProps={{ min: 1, max: 12 }}
            fullWidth
            disabled={mutation.isPending}
          />
          <TextField
            label="Day"
            variant="outlined"
            type="number"
            value={birthDay}
            onChange={(e) => handleChange("birth_day", e.target.value)}
            inputProps={{ min: 1, max: 31 }}
            sx={{ mb: 3 }}
            fullWidth
            disabled={mutation.isPending}
          />
        </Box>
      </Box>
    </Box>
  );
}
